import { NgIconComponent } from "@ng-icons/core";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  NgZone,
  OnDestroy,
  Output,
  Renderer2,
  ViewChild,
  inject,
} from "@angular/core";
import { CommonModule, DOCUMENT } from "@angular/common";
import { overlayAnchor, topOverlayZIndex } from "../overlay-anchor";
import { listenOutsideAngular } from "../utils/listen-outside-angular";

/**
 * EVA Popover - Composant 100% custom
 *
 * Panneau flottant au clic avec contenu riche.
 * Utilise uniquement les tokens EVA.
 *
 * @example
 * // Simple
 * <eva-popover>
 *   <button trigger>Ouvrir</button>
 *   <div content>Contenu du popover</div>
 * </eva-popover>
 *
 * // Avec header
 * <eva-popover header="Titre" position="bottom">
 *   <button trigger>Cliquez</button>
 *   <div content>
 *     <p>Contenu riche ici</p>
 *   </div>
 * </eva-popover>
 *
 * // Ancré sur un élément extérieur (bouton dans un autre composant, cellule de tableau…)
 * <eva-button label="Actions" (onClick)="op.toggle($event)"></eva-button>
 * <eva-popover #op>
 *   <div content>…</div>
 * </eva-popover>
 */
@Component({
  selector: "eva-popover",
  templateUrl: "./eva-popover.component.html",
  styleUrls: ["./eva-popover.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [NgIconComponent, CommonModule],
})
export class EvaPopoverComponent implements OnDestroy {
  // ==========================================================================
  // Inputs
  // ==========================================================================

  /** Titre du popover */
  @Input() header?: string;

  /** Position */
  @Input() position: "top" | "right" | "bottom" | "left" = "bottom";

  /** Alignement */
  @Input() align: "start" | "center" | "end" = "center";

  /** Déclencheur d'ouverture */
  @Input() trigger: "click" | "hover" | "focus" = "click";

  /** Fermer au clic externe */
  @Input() dismissible: boolean = true;

  /** Afficher une flèche directionnelle */
  @Input() showArrow: boolean = true;

  /** Largeur du popover */
  @Input() width: string = "280px";

  /** Largeur maximale du popover */
  @Input() maxWidth: string = "400px";

  /** Désactiver le popover */
  @Input() disabled: boolean = false;

  /** Afficher le bouton fermer */
  @Input() showClose: boolean = false;

  /**
   * Si "body", le panel est détaché et appendé à `document.body` à l'ouverture,
   * en `position: fixed`. Permet de sortir des stacking contexts et `overflow:
   * hidden` des ancêtres. Le positionnement est calculé via getBoundingClientRect()
   * du trigger. À utiliser quand le popover est imbriqué dans une page avec
   * scroll/overflow contraint (ex. modules en `eva-module--fullscreen`).
   *
   * Note : en mode `body`, le popover se ferme au scroll de la fenêtre car la
   * position est fixée au moment de l'ouverture. Les classes de positionnement
   * SCSS (`--top`, `--bottom`, etc.) ne sont pas appliquées : la flèche peut
   * donc être mal placée. Préférer `showArrow=false` dans ce mode.
   */
  @Input() appendTo: "inline" | "body" = "inline";

  // ==========================================================================
  // Outputs
  // ==========================================================================

  /** Émis quand le popover s'ouvre */
  @Output() onShow = new EventEmitter<void>();
  /** Émis quand le popover se ferme */
  @Output() onHide = new EventEmitter<void>();

  // ==========================================================================
  // ViewChild
  // ==========================================================================

  @ViewChild("popoverPanel") popoverPanel?: ElementRef<HTMLElement>;

  // ==========================================================================
  // State
  // ==========================================================================

  isVisible: boolean = false;
  private hoverTimeout?: ReturnType<typeof setTimeout>;
  private isAppendedToBody = false;
  private anchorRect?: DOMRect;
  private anchor: Element | null = null;
  private stopRepositionListener: (() => void) | null = null;
  private stopPageListeners: (() => void)[] = [];
  private readonly zone = inject(NgZone);

  constructor(
    private el: ElementRef,
    private cdr: ChangeDetectorRef,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private doc: Document,
  ) {}

  // ==========================================================================
  // Lifecycle
  // ==========================================================================

  ngOnDestroy(): void {
    this.clearTimeout();
    this.stopListeningPage();
    this.detachPanelFromBody();
  }

  // ==========================================================================
  // Events
  // ==========================================================================

  onDocumentClick(event: Event): void {
    if (!this.isVisible || !this.dismissible) return;

    const target = event.target as HTMLElement;
    if (this.el.nativeElement.contains(target)) return;
    if (this.anchor?.contains(target)) return;
    if (this.isAppendedToBody && this.popoverPanel?.nativeElement?.contains(target)) return;
    this.hide();
  }

  onEscapeKey(): void {
    if (this.isVisible) {
      this.hide();
    }
  }

  // En capture : eva-dialog stoppe la propagation du clic et d'Escape avant le document.
  private listenPage(): void {
    this.stopPageListeners = [
      listenOutsideAngular(this.zone, this.doc, "click", (event) => this.onDocumentClick(event), true),
      listenOutsideAngular(
        this.zone,
        this.doc,
        "keydown",
        (event) => {
          if ((event as KeyboardEvent).key !== "Escape" || !this.isVisible) return;
          event.preventDefault();
          event.stopPropagation();
          this.onEscapeKey();
        },
        true,
      ),
    ];
  }

  private stopListeningPage(): void {
    this.stopPageListeners.forEach((stop) => stop());
    this.stopPageListeners = [];
  }

  onTriggerClick(event: Event): void {
    if (this.disabled || this.trigger !== "click") return;
    event.stopPropagation();
    this.toggle();
  }

  onTriggerMouseEnter(): void {
    if (this.disabled || this.trigger !== "hover") return;
    this.clearTimeout();
    this.show();
  }

  onTriggerMouseLeave(): void {
    if (this.disabled || this.trigger !== "hover") return;
    this.hoverTimeout = setTimeout(() => {
      this.hide();
    }, 100);
  }

  onPopoverMouseEnter(): void {
    if (this.trigger === "hover") {
      this.clearTimeout();
    }
  }

  onPopoverMouseLeave(): void {
    if (this.trigger === "hover") {
      this.hoverTimeout = setTimeout(() => {
        this.hide();
      }, 100);
    }
  }

  onTriggerFocus(): void {
    if (this.disabled || this.trigger !== "focus") return;
    this.show();
  }

  onTriggerBlur(): void {
    if (this.disabled || this.trigger !== "focus") return;
    // Delay to allow clicking inside popover
    setTimeout(() => {
      if (!this.el.nativeElement.contains(document.activeElement)) {
        this.hide();
      }
    }, 100);
  }

  // ==========================================================================
  // Methods
  // ==========================================================================

  /** Ouvre le panneau ; avec un événement (ou `target`), il s'ancre sur l'élément cliqué / survolé. */
  show(event?: Event, target?: Element): void {
    if (this.disabled) return;
    const anchor = target ?? (event ? overlayAnchor(event) : null);

    if (this.isVisible) {
      if (anchor && anchor !== this.anchor) {
        this.anchor = anchor;
        this.attachPanelToBody();
        this.cdr.markForCheck();
      }
      return;
    }

    this.anchor = anchor;
    this.isVisible = true;
    this.listenPage();
    this.onShow.emit();
    this.cdr.markForCheck();

    if (this.appendTo === "body" || this.anchor) {
      // Une micro-tâche passe avant le tick de la zone : le *ngIf ne serait pas encore rendu.
      this.cdr.detectChanges();
      this.attachPanelToBody();
    }
  }

  hide(): void {
    if (!this.isVisible) return;

    this.stopListeningPage();
    this.detachPanelFromBody();
    this.anchor = null;
    this.isVisible = false;
    this.onHide.emit();
    this.cdr.markForCheck();
  }

  /** Ouvert depuis un autre élément que l'ancre courante : le panneau s'y déplace au lieu de se fermer. */
  toggle(event?: Event, target?: Element): void {
    const anchor = target ?? (event ? overlayAnchor(event) : null);
    if (this.isVisible && (!anchor || anchor === this.anchor)) {
      this.hide();
    } else {
      this.show(event, target);
    }
  }

  get arrowVisible(): boolean {
    return this.showArrow && !this.anchor;
  }

  private anchorElement(): Element | null {
    return this.anchor ?? this.el.nativeElement.querySelector(".eva-popover__trigger");
  }

  /** Coordonnées figées à l'ouverture : un scroll (même d'un conteneur) qui déplace l'ancre ferme. */
  private addRepositionListener(): void {
    if (this.stopRepositionListener) return;
    const onMove = () => {
      const rect = this.anchorElement()?.getBoundingClientRect();
      // Un scroll livré en retard (onglet caché) ne déplace pas l'ancre : le panneau reste aligné.
      const anchor = this.anchorRect;
      if (!rect || !anchor || Math.abs(rect.top - anchor.top) > 1 || Math.abs(rect.left - anchor.left) > 1) {
        this.hide();
      }
    };
    const stopScroll = listenOutsideAngular(this.zone, window, "scroll", onMove, true);
    const stopResize = listenOutsideAngular(this.zone, window, "resize", onMove);
    this.stopRepositionListener = () => {
      stopScroll();
      stopResize();
    };
  }

  private removeRepositionListener(): void {
    this.stopRepositionListener?.();
    this.stopRepositionListener = null;
  }

  private clearTimeout(): void {
    if (this.hoverTimeout) {
      clearTimeout(this.hoverTimeout);
      this.hoverTimeout = undefined;
    }
  }

  /**
   * Déplace le panel vers `document.body`, le passe en `position: fixed` et
   * calcule sa position via `getBoundingClientRect()` du trigger.
   * Permet au panel de sortir des stacking contexts et `overflow: hidden`
   * des ancêtres.
   */
  private attachPanelToBody(): void {
    const panelEl = this.popoverPanel?.nativeElement;
    if (!this.isVisible || !panelEl) return;
    const triggerEl = this.anchorElement();
    if (!triggerEl) return;

    this.renderer.appendChild(this.doc.body, panelEl);
    this.isAppendedToBody = true;
    this.renderer.setStyle(panelEl, "z-index", String(topOverlayZIndex()));

    // Reset positioning that comes from .eva-popover__panel--{position} classes
    this.renderer.setStyle(panelEl, "position", "fixed");
    this.renderer.setStyle(panelEl, "margin", "0");
    this.renderer.setStyle(panelEl, "right", "auto");
    this.renderer.setStyle(panelEl, "bottom", "auto");
    this.renderer.setStyle(panelEl, "transform", "none");
    // Cap la hauteur au viewport et active un scroll interne si le contenu déborde
    this.renderer.setStyle(panelEl, "max-height", "calc(100vh - 32px)");
    this.renderer.setStyle(panelEl, "overflow-y", "auto");

    const tr = triggerEl.getBoundingClientRect();
    // Taille de layout : le rect est réduit par le scale(0.95) de l'animation d'entrée.
    const pr = { width: panelEl.offsetWidth, height: panelEl.offsetHeight };
    let top = 0;
    let left = 0;

    switch (this.position) {
      case "bottom":
        top = tr.bottom + 10;
        if (top + pr.height > window.innerHeight - 8 && tr.top - pr.height - 10 >= 8) {
          top = tr.top - pr.height - 10;
        }
        left = tr.left + tr.width / 2 - pr.width / 2;
        if (this.align === "start") left = tr.left;
        if (this.align === "end") left = tr.right - pr.width;
        break;
      case "top":
        top = tr.top - pr.height - 10;
        left = tr.left + tr.width / 2 - pr.width / 2;
        if (this.align === "start") left = tr.left;
        if (this.align === "end") left = tr.right - pr.width;
        break;
      case "left":
        top = tr.top + tr.height / 2 - pr.height / 2;
        left = tr.left - pr.width - 10;
        if (this.align === "start") top = tr.top;
        if (this.align === "end") top = tr.bottom - pr.height;
        break;
      case "right":
        top = tr.top + tr.height / 2 - pr.height / 2;
        left = tr.right + 10;
        if (this.align === "start") top = tr.top;
        if (this.align === "end") top = tr.bottom - pr.height;
        break;
    }

    // Clamp dans le viewport (8px de marge)
    const margin = 8;
    if (left < margin) left = margin;
    if (left + pr.width > window.innerWidth - margin) {
      left = window.innerWidth - pr.width - margin;
    }
    if (top < margin) top = margin;
    if (top + pr.height > window.innerHeight - margin) {
      top = window.innerHeight - pr.height - margin;
    }

    this.renderer.setStyle(panelEl, "top", `${top}px`);
    this.renderer.setStyle(panelEl, "left", `${left}px`);

    this.anchorRect = triggerEl.getBoundingClientRect();
    this.addRepositionListener();
  }

  private detachPanelFromBody(): void {
    this.removeRepositionListener();
    if (!this.isAppendedToBody) return;
    const panelEl = this.popoverPanel?.nativeElement;
    if (panelEl?.parentElement === this.doc.body) {
      this.renderer.removeChild(this.doc.body, panelEl);
    }
    this.isAppendedToBody = false;
  }

  get popoverStyle(): { [key: string]: string } {
    return {
      width: this.width,
      maxWidth: this.maxWidth,
    };
  }
}
